import Button from '@/shared/Button';
import Paragraph from '@/shared/Paragraph';
import Div from '@/shared/Div';
import Form from '@/shared/Form';
import OtpInput from '@/shared/OtpInput';
import React, { useState, useEffect, memo } from 'react';
import { useForm } from 'react-hook-form';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { useRouter } from 'next/router';
import FixedWrapper from '@/shared/FixedWrapper';
import useScreen from '@/hooks/useScreen';
import classNames from 'classnames';
import useGTM from '@/hooks/useGTM';
import {
  addProfileInfo,
  updateProfileInfo,
  verifyAddProfileInfo,
  verifyUpdateProfileInfo,
} from '@/actions/ecom/profileV2';
import useToast from '@/hooks/useToast';
import {
  D365_USER_ID,
  FORMTYPES,
  PROFILE_MODAL_STYLING,
  SHOW_PROFILE_TOAST,
} from '@/constants/profileManagement';
import Cookies from '@/utils/cookie';

type ProfileOtpProps = {
  mobileNumber: string;
  boxNumber?: number;
  contactId?: string;
  isProfileUpdate?: boolean;
  formData?: any;
  formType?: string;
  name?: {
    firstName?: string;
    lastName?: string;
  };
};
type ButtonProps = {
  isLoading?: boolean;
  otp?: string;
  isMobile?: boolean;
  boxNumber?: number;
};

const renderButton = ({ isLoading, otp, isMobile, boxNumber }: ButtonProps) => {
  return (
    <Button
      className={classNames('w-full rounded', {
        'mt-16': !isMobile,
        [PROFILE_MODAL_STYLING?.btnClassName || '']: !!PROFILE_MODAL_STYLING?.btnClassName,
      })}
      type="submit"
      disabled={Boolean(isLoading || !otp || otp.length != boxNumber)}
      isLoading={isLoading}
    >
      Verify
    </Button>
  );
};

const Otp = ({
  mobileNumber,
  boxNumber = 4,
  contactId,
  isProfileUpdate = false,
  formData = {},
  formType = '',
  name = {},
}: ProfileOtpProps) => {
  const [otp, setOtp] = useState('');
  const [isVerifyButtonClicked, setVerifyButtonClicked] = useState(false);
  const [disableResend, setResendDisable] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(30);
  const [emptyInputBox, setEmptyInputBox] = useState(false);
  const { isMobile } = useScreen();
  const { triggerDefault } = useToast();

  const {
    handleSubmit,
    formState: { errors },
  } = useForm();
  const router = useRouter();

  const verifyAddProfile = (...args: any) => verifyAddProfileInfo.apply(null, args);
  const {
    isError: verifyAddError = false,
    promisifyTrigger: verifyAddPromisifyTrigger,
    error: addError,
    isLoading: verifyAddProfileLoading,
  } = useTriggerRequest({
    fetch: verifyAddProfile,
  });
  const verifyUpdateProfile = (...args: any) => verifyUpdateProfileInfo.apply(null, args);
  const {
    isError: verifyUpdateError = false,
    promisifyTrigger: verifyUpdateTrigger,
    error: updateError,
    isLoading: updateAddProfileLoading,
  } = useTriggerRequest({
    fetch: verifyUpdateProfile,
  });

  useEffect(() => {
    let countdownTimer: NodeJS.Timeout;

    if (disableResend && resendCountdown > 0) {
      countdownTimer = setTimeout(() => {
        setResendCountdown((prevCount) => prevCount - 1);
      }, 1000);
    } else if (resendCountdown === 0 && (!verifyAddProfileLoading || !updateAddProfileLoading)) {
      setResendDisable(false);
    }

    return () => {
      clearTimeout(countdownTimer);
    };
  }, [disableResend, resendCountdown, updateAddProfileLoading, verifyAddProfileLoading]);

  const isValid = (otp: string) => {
    if (!new RegExp('^[0-9]{' + boxNumber + '}$').test(otp)) {
      <Paragraph>Enter a valid OTP</Paragraph>;
      return false;
    }
    return true;
  };

  const handleResendClick = async () => {
    try {
      setVerifyButtonClicked(false);
      setOtp('');
      setEmptyInputBox(true);

      if (isProfileUpdate) {
        await updateProfileInfo({
          contactId,
          contact: formData?.mobileNumber || formData.email,
          name:
            formType === FORMTYPES.EDIT_EMAIL
              ? `${name?.firstName} ${name?.lastName}`
              : formData?.name,
        });
      } else if (formType === FORMTYPES.EDIT_SECONDARY_USER || formType === FORMTYPES.EDIT_EMAIL) {
        await addProfileInfo({
          d365UserId: Cookies.get(D365_USER_ID) || '',
          contact: formData?.mobileNumber || formData?.email,
          name:
            formType === FORMTYPES.EDIT_EMAIL
              ? `${name?.firstName} ${name?.lastName}`
              : formData?.name,
        });
      }

      setResendCountdown(30);
      setResendDisable(true);
      setEmptyInputBox(false);
    } catch (error) {
      console.error('Error resending OTP:', error);
    }
  };

  const onSubmit = async () => {
    setResendDisable(true);
    setVerifyButtonClicked(true);
    let verifyResult;
    if (isValid(otp)) {
      if (isProfileUpdate) {
        verifyResult = await verifyUpdateTrigger({
          contactId,
          otp,
        });
      } else {
        verifyResult = await verifyAddPromisifyTrigger({
          d365UserId: Cookies.get(D365_USER_ID) || '',
          otp,
        });
      }
      if (verifyResult?.status === 200 || verifyResult?.status === 201) {
        sessionStorage.setItem(SHOW_PROFILE_TOAST, 'true');
        window.location.reload();
      }
    }
  };

  const setOTP = () => setOtp;

  useEffect(() => {
    if (isProfileUpdate && verifyUpdateError && updateError?.error) {
      triggerDefault({
        message: updateError?.error,
        durationInms: 5000,
      });
    } else if (verifyAddError && addError?.error) {
      triggerDefault({
        message: addError?.error,
        durationInms: 5000,
      });
    }
  }, [verifyAddError, addError?.error, verifyUpdateError, updateError?.error]);

  return (
    <Div
      className={classNames(
        'mt-8 md:p-10 md:bg-white md:rounded-xl  md:border md:border-core-outline-subtle',
        [PROFILE_MODAL_STYLING.className],
      )}
    >
      <Div className="flex flex-col gap-2 mb-8">
        <Paragraph
          className={classNames('text-h-1 md:text-hd-1 text-black/70', [
            PROFILE_MODAL_STYLING.titleClassName,
          ])}
        >
          Enter OTP
        </Paragraph>
        <Paragraph className="text-black/50">OTP sent to {mobileNumber}.</Paragraph>
      </Div>
      <Form name="otpForm" onSubmit={handleSubmit(onSubmit)}>
        <OtpInput
          data-testid="otpInput"
          name="otpLogin"
          boxNumber={boxNumber}
          value={otp}
          onSet={setOTP()}
          errors={errors}
          emptyInputBox={emptyInputBox}
        />
        <Button
          theme="link"
          size="sm"
          className="text-blue-400 py-3 !px-0"
          onClick={handleResendClick}
          disabled={disableResend}
          data-testId="resendOtp"
        >
          {resendCountdown ? `Resend OTP (${resendCountdown}s)` : 'Resend OTP'}
        </Button>

        {isMobile ? (
          <FixedWrapper
            className={classNames('md:hidden p-6 pt-4', [PROFILE_MODAL_STYLING.mobileBtnClassName])}
          >
            {renderButton({
              isLoading: verifyAddProfileLoading || updateAddProfileLoading,
              otp,
              isMobile: true,
              boxNumber,
            })}
          </FixedWrapper>
        ) : (
          renderButton({
            isLoading: verifyAddProfileLoading || updateAddProfileLoading,
            otp,
            isMobile: false,
            boxNumber,
          })
        )}
      </Form>
    </Div>
  );
};

export default memo(Otp);
